import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, filter, NEVER, Observable, of, Subscription, withLatestFrom } from 'rxjs';
import { distinctUntilChanged, map, startWith, switchMap, take } from 'rxjs/operators';

import {
  CmsCollection,
  CmsContent,
  CmsContentType,
  CmsPlaceholder,
  CmsTarget,
  CmsTaxonomyGeneral,
  isCmsArticle,
} from '@fcom/core-api';
import { CmsLink, CmsTeaser } from '@fcom/core-api/interfaces';
import {
  AspectRatioString,
  MediaQueryService,
  PriceCalendarCTAParams,
  PriceCalendarService,
  TestVariant,
  TripType,
} from '@fcom/common';
import { NON_EUROPE_SITES } from '@fcom/core/constants';
import { LoaderTheme, PromoTheme, toCamelCase } from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';
import { stopPropagation, unsubscribe } from '@fcom/core/utils';
import { DestinationSearchBlockOffer } from '@fcom/cms/interfaces';
import { AppState, CmsDataService, GlobalBookingTravelClass } from '@fcom/core';
import { ElementActions, ElementTypes, GaContext, PriceCalendarParams } from '@fcom/common/interfaces';
import { GtmService } from '@fcom/common/gtm';
import { finShare } from '@fcom/rx';
import { CampaignBannerService } from '@fcom/common/services/campaign-banner/campaign-banner.service';
import {
  DestinationSearchFeatureState,
  DestinationSearchFilterStateMeta,
  DestinationSearchViewStyle,
  PriceLoadingState,
  SortBehavior,
} from '@fcom/destination-search/interfaces';
import { CmsTripTypeMap } from '@fcom/core/utils/tripType.utils';
import { LoginStatus, Profile } from '@fcom/core-api/login';
import { loginStatus, profile } from '@fcom/core/selectors';

import { CampaignRibbon, Continent, RibbonTheme } from '../../interfaces';
import { filterState, roundTripPricesState } from '../../store';
import { DestinationSearchOffersService } from '../../services/destination-search-offers.service';
import { setFromLocationCode, setRestrictToDestinations } from '../../store/actions/destination-search-filter.actions';
import { OriginSelectedEvent } from '../destination-search-origin-selector/destination-search-origin-selector.component';

export interface CategorizedDestination {
  [key: string]: {
    destinations: DestinationSearchBlockOffer[];
    categoryLabel: string;
    promo$?: Observable<CmsContent>;
  };
}

@Component({
  selector: 'fin-destination-search-results',
  templateUrl: 'destination-search-results.component.html',
  styleUrls: ['destination-search-results.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DestinationSearchResultsComponent implements OnInit, OnDestroy {
  @Input()
  content: CmsPlaceholder;

  @Input()
  meta: DestinationSearchFilterStateMeta;

  @Input()
  filteredDestinationsWithOffers$: Observable<DestinationSearchBlockOffer[]>;

  @Input()
  viewStyle: DestinationSearchViewStyle = DestinationSearchViewStyle.TILES; // default and only one until next iteration

  @Input()
  selectedOriginLocationCode$: Observable<string>;

  @Input()
  isFilterApplied$: Observable<boolean>;

  @Output()
  clearAllFilters = new EventEmitter<void>();

  readonly PriceLoadingState = PriceLoadingState;
  readonly LoaderTheme = LoaderTheme;
  readonly PromoTheme = PromoTheme;
  readonly CmsTaxonomyGeneral = CmsTaxonomyGeneral;
  readonly CmsContentType = CmsContentType;
  readonly NEVER = NEVER;
  readonly CmsTripTypeMap = CmsTripTypeMap;
  readonly TestVariant = TestVariant;

  SORTING = SortBehavior;

  sortedDestinations$: Observable<DestinationSearchBlockOffer[]>;
  sortOrder$: BehaviorSubject<SortBehavior>;
  subscriptions: Subscription = new Subscription();
  sortByValues: string[];
  updatingPricesLoader$: Observable<PriceLoadingState>;

  isMobile$: Observable<boolean>;
  identifier: string;

  ribbon$: Observable<CampaignRibbon>;

  topPromoItems$: Observable<CmsContent[]>;
  bottomPromoItems$: Observable<CmsContent[]>;
  categorizedDestinations$: Observable<CategorizedDestination>;
  categoryList$: Observable<(Continent | string)[]>;
  aspectRatioForPromo$: Observable<AspectRatioString> = NEVER;
  aspectRatioForSearchItems$: Observable<AspectRatioString> = NEVER;
  customDestinations$: Observable<string[]>;
  customContinents: string[];
  disableSectionHeadings = false;
  profile$: Observable<Profile>;
  showRoundTripPrices$: Observable<boolean> = of(false);
  calendarOpen$: Observable<PriceCalendarParams | null> = NEVER;

  constructor(
    private languageService: LanguageService,
    private store$: Store<DestinationSearchFeatureState>,
    private appStore$: Store<AppState>,
    private gtmService: GtmService,
    private destinationSearchOffersService: DestinationSearchOffersService,
    private mediaQueryService: MediaQueryService,
    private cmsDataService: CmsDataService,
    private campaignBannerService: CampaignBannerService,
    private priceCalendarService: PriceCalendarService
  ) {
    this.calendarOpen$ = this.priceCalendarService.calendarOpen$;
  }

  ngOnInit(): void {
    this.showRoundTripPrices$ = this.store$.pipe(roundTripPricesState(this.meta.id), startWith(false), finShare());

    this.profile$ = this.appStore$.pipe(
      loginStatus(),
      filter(
        (memberLoginStatus) => memberLoginStatus !== LoginStatus.INITIAL && memberLoginStatus !== LoginStatus.PENDING
      ),
      switchMap((memberLoginStatus) =>
        memberLoginStatus === LoginStatus.LOGGED_IN ? this.appStore$.pipe(profile()) : of(undefined)
      ),
      finShare()
    );

    this.topPromoItems$ = this.content?.localSettings?.topPromoItems?.jsonUrl
      ? this.fetchPromoItems(this.content?.localSettings?.topPromoItems?.jsonUrl)
      : NEVER;

    this.bottomPromoItems$ = this.content?.localSettings?.bottomPromoItems?.jsonUrl
      ? this.fetchPromoItems(this.content?.localSettings?.bottomPromoItems?.jsonUrl)
      : NEVER;

    this.identifier = this.meta.id;
    this.updatingPricesLoader$ = this.destinationSearchOffersService.loaderStatus$;
    this.isMobile$ = this.mediaQueryService.isBreakpoint$('mobile');
    this.aspectRatioForPromo$ = this.isMobile$.pipe(map((isMobile) => (isMobile ? '4x3' : '21x9')));
    this.aspectRatioForSearchItems$ = this.isMobile$.pipe(map((isMobile) => (isMobile ? '4x3' : '16x9')));
    this.disableSectionHeadings = this.content?.localSettings?.disableSectionHeadings;
    this.customContinents = this.content?.localSettings?.restrictToDestinations?.continent;

    this.customDestinations$ = this.selectedOriginLocationCode$.pipe(
      map(
        (selectedOriginLocationCode) =>
          this.content?.localSettings?.restrictToDestinations?.destination ??
          this.content?.localSettings?.restrictToDestinations?.destinationsByOrigin[selectedOriginLocationCode]
      )
    );

    this.sortByValues = [SortBehavior.ALPHABETICAL, SortBehavior.PRICE];
    const sortBehavior: SortBehavior =
      SortBehavior[this.content?.localSettings?.selectedSortBy] ?? SortBehavior.ALPHABETICAL;
    this.sortOrder$ = new BehaviorSubject(sortBehavior);

    if (this.content?.localSettings?.ribbon?.jsonUrl) {
      this.campaignBannerService.setCampaignBannerVisibility(false);
      this.ribbon$ = this.cmsDataService.getFragmentJson(this.content.localSettings.ribbon.jsonUrl).pipe(
        filter(Boolean),
        map((content: CmsTeaser) => ({
          bookByText: content?.localSettings?.bookByText,
          travelPeriodText: content?.localSettings?.travelPeriodText,
          termsAndConditionText: content?.teaserText,
          termsAndConditionLink$: this.cmsDataService
            .getFragmentJson(content?.target?.jsonUrl)
            .pipe(map((target: CmsTeaser) => target?.url)),
          theme: content?.subjectTaxonomyTags?.find(
            (tag) => tag === CmsTaxonomyGeneral.BLACK_FRIDAY || tag === CmsTaxonomyGeneral.HEATHER_BG
          ) as RibbonTheme,
        }))
      );
    } else {
      this.ribbon$ = NEVER;
    }

    /* Sort destinations, alphabetical order by default. Custom order only works when disableSectionHeadings is true
      If restrictToDestinations is set, then use the list instead of normal offer destination list*/
    this.sortedDestinations$ = combineLatest([this.filteredDestinationsWithOffers$, this.customDestinations$]).pipe(
      map(([destinations, customDestinations]) =>
        customDestinations
          ? customDestinations
              .map((countryCode) => destinations.find((destination) => destination.locationCode === countryCode))
              .filter(Boolean)
          : destinations
      ),
      switchMap((destinations) =>
        this.languageService.countryCode.pipe(
          map((countryCode) => {
            const targetCountry = destinations.find((destination) => destination.flag === countryCode.toLowerCase());
            return targetCountry && targetCountry.continent !== Continent.EUROPE
              ? destinations.filter((destination) => destination.continent !== targetCountry.continent)
              : destinations;
          })
        )
      ),
      withLatestFrom(this.sortOrder$.pipe(distinctUntilChanged())),
      map(([destinations, sorting]) => {
        if (sorting === SortBehavior.ALPHABETICAL) {
          return destinations.sort(this.sortByAlphabetical);
        }
        if (sorting === SortBehavior.PRICE) {
          return destinations.sort(this.sortByPrice);
        }
        if (sorting === SortBehavior.CUSTOM && this.disableSectionHeadings) {
          return destinations;
        }
        return destinations.sort(this.sortByAlphabetical);
      }),
      map((destinations) => this.sortByValidPriceFirst(destinations))
    );
    /*
    if disableSectionHeadings is true, then no need to group the destination by continents
    if restrictToDestinations.continent is set, then use the list
    */
    this.categoryList$ = this.languageService.countryCode.pipe(
      switchMap((countryCode) =>
        this.disableSectionHeadings
          ? of(['mixed'])
          : !this.customContinents
            ? of(
                [
                  countryCode === 'FI' ? 'finland' : undefined,
                  Continent.EUROPE,
                  Continent.MIDDLE_EAST,
                  Continent.AMERICA,
                  Continent.ASIA_PACIFIC,
                  Continent.AFRICA,
                ].filter(Boolean)
              )
            : of(this.customContinents)
      ),
      map((continents) => continents.map((continent) => toCamelCase(continent)))
    );

    const continentPromoItems = this.content?.localSettings?.continentPromoItems
      ? Object.keys(this.content.localSettings.continentPromoItems).reduce(
          (init, continent) => ({
            ...init,
            [continent]: this.content?.localSettings?.continentPromoItems[continent]?.jsonUrl
              ? this.fetchContinentPromoItem(this.content?.localSettings?.continentPromoItems[continent]?.jsonUrl)
              : NEVER,
          }),
          {}
        )
      : null;

    this.categorizedDestinations$ = combineLatest([
      this.sortedDestinations$,
      this.languageService.countryCode,
      this.categoryList$,
    ]).pipe(
      map(
        ([destinations, countryCode, categoryList]: [DestinationSearchBlockOffer[], string, (Continent | string)[]]) =>
          destinations
            .filter(Boolean)
            .map((destination) => ({
              ...destination,
              isShortHaul: NON_EUROPE_SITES.includes(this.languageService.langValue)
                ? false
                : destination.continent === Continent.EUROPE,
            }))
            .reduce(
              (init, destination) => {
                const key = this.disableSectionHeadings
                  ? 'mixed'
                  : countryCode === 'FI' && destination.flag === 'fi'
                    ? 'finland'
                    : toCamelCase(destination.continent);
                init[key]?.destinations?.push(destination);
                return init;
              },
              Object.fromEntries(
                categoryList.map((k) => [
                  k,
                  {
                    destinations: [],
                    categoryLabel: `destinationSearch.continentTitle.${toCamelCase(k)}`,
                    promo$: continentPromoItems?.[k],
                  },
                ])
              ) as CategorizedDestination
            )
      ),
      map((destinations) =>
        //filter out continent has empty destination list
        Object.fromEntries(Object.entries(destinations).filter(([_, v]) => v.destinations?.length > 0))
      )
    );
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions = unsubscribe(this.subscriptions);
    }
    this.campaignBannerService.setCampaignBannerVisibility(true);
  }

  trackByFn(_index: number, item: DestinationSearchBlockOffer): string {
    return item.destination;
  }

  fetchPromoItems(url: string): Observable<CmsContent[]> {
    return this.profile$.pipe(
      switchMap((memberProfile) =>
        this.cmsDataService.getFragmentJson(url, { tier: memberProfile?.tier || '' }).pipe(
          map((content: CmsCollection<CmsLink>) => content?.items.map((item) => item.jsonUrl)),
          switchMap((urls: string[]) => this.getCollectionItems(urls)),
          switchMap((items: CmsContent[]) =>
            combineLatest(
              items.map((item: CmsContent) =>
                isCmsArticle(item)
                  ? this.getArticleButton(item).pipe(
                      map((targets) => ({ ...item, target: targets?.length ? targets[0] : undefined }))
                    )
                  : of(item)
              )
            )
          )
        )
      ),
      finShare()
    );
  }

  fetchContinentPromoItem(url: string): Observable<CmsContent> {
    return this.cmsDataService.getFragmentJson(url).pipe(
      switchMap((item: CmsContent) =>
        isCmsArticle(item)
          ? this.getArticleButton(item).pipe(
              map((targets) => ({ ...item, target: targets?.length ? targets[0] : undefined }))
            )
          : of(item)
      ),
      switchMap((item: CmsContent) =>
        item?.firstMedia?.jsonUrl
          ? this.cmsDataService
              .getFragmentJson(item.firstMedia.jsonUrl)
              .pipe(map((mediaContent) => ({ ...item, media: [mediaContent] })))
          : of({ ...item, media: [] })
      )
    );
  }

  calendarClicked(
    selectedOriginLocationCode: string,
    [event, destinationItem, travelClass, tripType]: [
      Event,
      DestinationSearchBlockOffer,
      GlobalBookingTravelClass,
      TripType,
    ]
  ): void {
    stopPropagation(event);
    this.priceCalendarService.openPriceCalendar(
      selectedOriginLocationCode,
      destinationItem.locationCode,
      tripType,
      travelClass,
      destinationItem.titleMaster,
      destinationItem.discountCode
    );
  }

  priceCalendarCTAClicked(params: PriceCalendarCTAParams): void {
    this.priceCalendarService.priceCalendarCTAClicked(params);
  }

  closePriceCalendar(): void {
    this.priceCalendarService.closePriceCalendar();
  }

  destinationClick([destinationBlockOffer, travelClass, offerTripType]: [
    DestinationSearchBlockOffer,
    GlobalBookingTravelClass,
    TripType,
  ]): void {
    this.subscriptions.add(
      this.store$
        .pipe(filterState(this.identifier), take(1), withLatestFrom(this.selectedOriginLocationCode$))
        .subscribe(([{ tripType, tripDates }, selectedOriginLocationCode]) => {
          const selectedTripType = offerTripType || tripType;
          this.priceCalendarService.prefillBookingWidget(
            travelClass,
            tripDates,
            selectedTripType,
            destinationBlockOffer.locationCode,
            selectedOriginLocationCode,
            destinationBlockOffer?.discountCode?.code
          );
          this.gtmService.trackElement(
            `tile-${destinationBlockOffer.titleMaster.replace(/ /g, '_')}-${travelClass}-${this.CmsTripTypeMap[selectedTripType]}`,
            GaContext.DESTINATION_MAIN_PAGE,
            ElementTypes.LIST_ITEM,
            undefined,
            ElementActions.CLICK
          );
        })
    );
  }

  flightHotelClick(destinationItem): void {
    this.gtmService.trackElement(
      `tile-${destinationItem.titleMaster.replace(/ /g, '_')}-flightHotel`,
      GaContext.DESTINATION_MAIN_PAGE,
      ElementTypes.LIST_ITEM,
      undefined,
      ElementActions.CLICK
    );
  }

  onOriginSelected(event: OriginSelectedEvent): void {
    if (event.destinations?.length) {
      this.store$.dispatch(setRestrictToDestinations({ restrictToDestinations: event.destinations, meta: this.meta }));
    }
    this.store$.dispatch(setFromLocationCode({ fromLocationCode: event.origin, meta: this.meta }));

    this.gtmService.trackElement(
      'origin-selector',
      GaContext.LANDING_PAGE,
      ElementTypes.SELECT_BOX,
      event.origin,
      ElementActions.CLICK
    );
  }

  trackByCmsContent(index: number, content: CmsContent): number {
    return content.contentId ?? index;
  }

  private sortByAlphabetical(a: DestinationSearchBlockOffer, b: DestinationSearchBlockOffer) {
    return a.title > b.title ? 1 : -1;
  }

  private sortByPrice(a: DestinationSearchBlockOffer, b: DestinationSearchBlockOffer) {
    const compare = a.price ? a.price : Infinity;
    const newPrice = b.price ? b.price : Infinity;

    return compare > newPrice ? 1 : -1;
  }

  private sortByValidPriceFirst = (destinations: DestinationSearchBlockOffer[]): DestinationSearchBlockOffer[] =>
    destinations
      .filter((i) => i.onewayPrice || i.returnPrice || i.price)
      .concat(destinations.filter((i) => !i.onewayPrice && !i.returnPrice && !i.price));

  private getCollectionItems(urls: string[]): Observable<CmsContent[]> {
    return combineLatest(
      urls.map((url) => this.cmsDataService.getFragmentJson<CmsContent>(url, { nestingDepth: '1', duplicates: 'true' }))
    );
  }

  private getArticleButton(article: CmsContent): Observable<CmsTarget[]> {
    if (!article.related?.length) {
      return of([]);
    }

    const data = article.related.map(
      (item: CmsTeaser) =>
        ({
          title: item.teaserTitle,
          openInNewTab: item.openInNewTab,
          url: (item.target as any as CmsLink).jsonUrl,
        }) as CmsTarget
    );

    return combineLatest(
      data.map((items) => this.cmsDataService.getFragmentJson<CmsContent | CmsTeaser>(items.url))
    ).pipe(
      map((targets) =>
        data.map((item, index) => ({
          ...item,
          url: targets[index].url,
          openInNewTab:
            (targets[index] as CmsTeaser).openInNewTab !== undefined
              ? (targets[index] as CmsTeaser).openInNewTab
              : item.openInNewTab,
        }))
      )
    );
  }
}
